import type { Capability } from '@/auth/capabilities';
import type { Profile } from '@/auth/profiles';
import type { PartnerContext, PartnerTier } from '@/partner/PartnerContext';

export interface PolicyContext {
  readonly can: (capability: Capability) => boolean;
  readonly profiles: readonly Profile[];
  readonly partner: PartnerContext;
  readonly features: Readonly<Record<string, boolean>>;
}

export abstract class Policy {
  abstract isSatisfiedBy(context: PolicyContext): boolean;

  abstract describe(): string;

  and(other: Policy): Policy {
    return new AllOf([this, other]);
  }

  or(other: Policy): Policy {
    return new AnyOf([this, other]);
  }

  not(): Policy {
    return new Not(this);
  }

  static always(): Policy {
    return new Always();
  }

  static capability(capability: Capability): Policy {
    return new HasCapability(capability);
  }

  static tier(tier: PartnerTier): Policy {
    return new HasTier(tier);
  }

  static feature(key: string): Policy {
    return new FeatureEnabled(key);
  }

  static onlyProfiles(allowed: readonly Profile[]): Policy {
    return new OnlyProfiles(allowed);
  }
}

class Always extends Policy {
  isSatisfiedBy(): boolean {
    return true;
  }

  describe(): string {
    return 'siempre';
  }
}

class HasCapability extends Policy {
  constructor(private readonly capability: Capability) {
    super();
  }

  isSatisfiedBy({ can }: PolicyContext): boolean {
    return can(this.capability);
  }

  describe(): string {
    return this.capability;
  }
}

class HasTier extends Policy {
  constructor(private readonly tier: PartnerTier) {
    super();
  }

  isSatisfiedBy({ partner }: PolicyContext): boolean {
    return partner.tier === this.tier;
  }

  describe(): string {
    return `tier ${this.tier}`;
  }
}

class FeatureEnabled extends Policy {
  constructor(private readonly key: string) {
    super();
  }

  isSatisfiedBy({ features }: PolicyContext): boolean {
    return features[this.key] ?? false;
  }

  describe(): string {
    return `feature ${this.key}`;
  }
}

class OnlyProfiles extends Policy {
  constructor(private readonly allowed: readonly Profile[]) {
    super();
  }

  isSatisfiedBy({ profiles }: PolicyContext): boolean {
    return (
      profiles.length > 0 &&
      profiles.every(profile => this.allowed.includes(profile))
    );
  }

  describe(): string {
    return `solo perfiles ${this.allowed.join(', ')}`;
  }
}

class Not extends Policy {
  constructor(private readonly policy: Policy) {
    super();
  }

  isSatisfiedBy(context: PolicyContext): boolean {
    return !this.policy.isSatisfiedBy(context);
  }

  describe(): string {
    return `no ${this.policy.describe()}`;
  }
}

class AllOf extends Policy {
  constructor(private readonly policies: readonly Policy[]) {
    super();
  }

  isSatisfiedBy(context: PolicyContext): boolean {
    return this.policies.every(policy => policy.isSatisfiedBy(context));
  }

  describe(): string {
    return this.policies.map(policy => policy.describe()).join(' y ');
  }
}

class AnyOf extends Policy {
  constructor(private readonly policies: readonly Policy[]) {
    super();
  }

  isSatisfiedBy(context: PolicyContext): boolean {
    return this.policies.some(policy => policy.isSatisfiedBy(context));
  }

  describe(): string {
    return this.policies.map(policy => policy.describe()).join(' o ');
  }
}
